import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Sparkles, 
  Heart, 
  Brain, 
  Zap,
  Star,
  Users,
  Shield,
  Globe,
  ArrowRight,
  Play,
  Moon,
  Target,
  CheckCircle,
  ChevronRight,
  MessageCircle
} from 'lucide-react'
import { useUserStore } from '../stores/userStore'
import StarAnimation from '../components/StarAnimation'
import JoinList from '../components/JoinList'

const Home = () => {
  const { user } = useUserStore()

  const heroFeatures = [
    { icon: Moon, text: 'AI-Powered Astrology' },
    { icon: Heart, text: 'Wellness Integration' },
    { icon: Target, text: 'Compatibility Matching' }
  ]

  const features = [
    {
      icon: Sparkles,
      title: 'AI Astrology Engine',
      description: 'Advanced machine learning algorithms analyze your birth chart and planetary positions to provide personalized astrological insights.',
      color: 'from-purple-500 to-pink-500',
      gradient: 'bg-gradient-to-br from-purple-500/20 to-pink-500/20'
    },
    {
      icon: Heart,
      title: 'Holistic Wellness',
      description: 'Seamlessly integrates astrology with Ayurveda, yoga, meditation, and modern wellness practices for complete mind-body harmony.',
      color: 'from-green-500 to-blue-500',
      gradient: 'bg-gradient-to-br from-green-500/20 to-blue-500/20'
    },
    {
      icon: Brain,
      title: 'Predictive Analytics',
      description: 'ML-powered pattern recognition provides predictive wellness recommendations and life guidance based on cosmic patterns.',
      color: 'from-orange-500 to-red-500',
      gradient: 'bg-gradient-to-br from-orange-500/20 to-red-500/20'
    },
    {
      icon: Zap,
      title: 'Real-time Insights',
      description: 'Instant readings and recommendations based on current planetary positions and your unique astrological profile.',
      color: 'from-yellow-500 to-orange-500',
      gradient: 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20'
    },
    {
      icon: MessageCircle,
      title: 'Live Chat with AI',
      description: 'Real-time text and voice conversations with our AI astrologer for instant guidance, chart analysis, and personalized recommendations.',
      color: 'from-green-500 to-blue-500',
      gradient: 'bg-gradient-to-br from-green-500/20 to-blue-500/20'
    },
    {
      icon: Users,
      title: 'Voice Assistant',
      description: 'Natural language AI assistant for instant astrological guidance, wellness tips, and personalized recommendations.',
      color: 'from-blue-500 to-cyan-500',
      gradient: 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20'
    },
    {
      icon: Target,
      title: 'Compatibility Matching',
      description: 'Advanced algorithms match you with compatible partners based on astrological compatibility and shared wellness goals.',
      color: 'from-indigo-500 to-purple-500',
      gradient: 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20'
    }
  ]

  const benefits = [
    'Personalized astrological insights',
    'AI-powered wellness recommendations',
    'Compatibility matching algorithms',
    'Voice-enabled guidance',
    'Real-time planetary analysis',
    'Holistic mind-body integration'
  ]

  const stats = [
    { icon: Users, label: 'Active Users', value: '10K+', color: 'from-blue-500 to-cyan-500' },
    { icon: Star, label: 'Readings Given', value: '50K+', color: 'from-purple-500 to-pink-500' },
    { icon: Shield, label: 'Data Security', value: '100%', color: 'from-green-500 to-emerald-500' },
    { icon: Globe, label: 'Countries', value: '25+', color: 'from-orange-500 to-red-500' }
  ]

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Wellness Coach",
      content: "RRAASI has transformed how I approach wellness. The AI insights combined with traditional astrology are incredibly accurate.",
      rating: 5
    },
    {
      name: "Michael Rodriguez",
      role: "Tech Entrepreneur",
      content: "The compatibility matching feature helped me find my perfect partner. The AI analysis was spot-on!",
      rating: 5
    },
    {
      name: "Priya Patel",
      role: "Yoga Instructor",
      content: "The integration of Ayurveda with modern AI technology is revolutionary. My clients love the personalized recommendations.",
      rating: 5
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative overflow-hidden mobile-section"
      >
        {/* Star Animation */}
        <StarAnimation starCount={30} />
        
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-blue-900/30 to-pink-900/50"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            >
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                RRAASI
              </span>
              <br />
              <span className="text-white">Cosmic Intelligence Platform</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
            >
              Where ancient wisdom meets modern AI. Discover your cosmic path with personalized astrological insights, 
              wellness guidance, and compatibility matching powered by advanced machine learning.
            </motion.p>

            {/* Hero Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap justify-center gap-6 mb-8"
            >
              {heroFeatures.map((feature, index) => (
                <div key={index} className="flex items-center space-x-2 text-gray-300">
                  <feature.icon className="w-5 h-5 text-purple-400" />
                  <span className="text-sm sm:text-base">{feature.text}</span>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              {user ? (
                <>
                  <Link
                    to="/astrology"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <span>Get Your Reading</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/live-personal-chat"
                    className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <span>Live Chat with AI</span>
                    <MessageCircle className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/wellness"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 hover:bg-white/20"
                  >
                    <span>Wellness Dashboard</span>
                    <Heart className="w-5 h-5" />
                  </Link>
                </>
              ) : (
                <>
                  <Link
                    to="/profile"
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <span>Start Your Journey</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/profile"
                    className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <span>Try Live Chat</span>
                    <MessageCircle className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/astrology"
                    className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 hover:bg-white/20"
                  >
                    <span>Learn More</span>
                    <Play className="w-5 h-5" />
                  </Link>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Join List Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        className="py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-4xl mx-auto">
          <JoinList />
        </div>
      </motion.section>

      {/* Features Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mobile-section py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                      <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Revolutionary AI-Powered Features
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Experience the future of astrology and wellness with RRAASI's cutting-edge AI technology
              </p>
            </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className={`${feature.gradient} rounded-2xl p-8 border border-white/10 backdrop-blur-sm hover:scale-105 transition-all duration-300`}
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center mb-6`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="mobile-section py-20 bg-gradient-to-r from-purple-900/20 to-blue-900/20"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.3 }}
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Why Choose RRAASI?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Our platform combines the wisdom of ancient traditions with the power of modern AI to provide 
                you with the most accurate and personalized guidance available.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.4 + index * 0.1 }}
                    className="flex items-center space-x-3"
                  >
                    <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.3 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-8 border border-white/10 backdrop-blur-sm">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">AI-Powered Accuracy</h3>
                  <p className="text-gray-300 mb-6">
                    Our advanced algorithms analyze thousands of data points to provide insights with unprecedented accuracy.
                  </p>
                  <div className="flex justify-center space-x-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">99.8%</div>
                      <div className="text-sm text-gray-400">Accuracy Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-white">24/7</div>
                      <div className="text-sm text-gray-400">Availability</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="mobile-section py-20"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * index }}
                className="text-center"
              >
                <div className={`w-20 h-20 bg-gradient-to-r ${stat.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <stat.icon className="w-10 h-10 text-white" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-300 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="mobile-section py-20 bg-gradient-to-r from-purple-900/20 to-blue-900/20"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.9 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-300">
              Join thousands of satisfied users who have transformed their lives
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.0 + index * 0.1 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-semibold text-white">{testimonial.name}</div>
                  <div className="text-sm text-gray-400">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.2 }}
        className="mobile-section py-20"
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.3 }}
            className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-3xl p-12 border border-white/10 backdrop-blur-sm"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Discover Your Cosmic Path?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join thousands of users who have transformed their lives with RRAASI's AI-powered astrological insights and wellness guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={user ? "/astrology" : "/profile"}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center space-x-2 transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <span>{user ? "Get Your Reading" : "Start Your Journey"}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/subscription"
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-2 transition-all duration-300 hover:bg-white/20"
              >
                <span>View Plans</span>
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  )
}

export default Home 